import type {Locale} from "i18n/localePreference";
import {numberFormat} from "core/format/intlFormats";

/**
 * Pure progress math for the v3 upload progress screen (ticket #20): overall/per-file percentages
 * and the design's speed/ETA formatting (design `speedStr`/`etaStr`, rendered as "11.6 MB/s ·
 * 8m left").
 *
 * The rolling-window `SpeedTracker` that used to live here was replaced in ui#942 by
 * {@link ./progressReadout}: a window whose oldest sample keeps dropping out re-reads a different
 * span of the transfer on every event, which is precisely what made the displayed speed flash and
 * the ETA oscillate.
 */

/** Byte-level upload progress, threaded from EncryptedUploadClient → TransferService → CreateFlow. */
export interface UploadProgress {
	/** Cumulative bytes sent across every file. */
	bytesSent: number;
	/** Total bytes of the whole selection. */
	totalBytes: number;
	/** Bytes sent per file, indexed like the selected files array. */
	perFileBytes: number[];
}

/** Zeroed progress for a fresh upload of the given selection. */
export function emptyProgress(files: File[]): UploadProgress {
	return {
		bytesSent: 0,
		totalBytes: files.reduce((sum, file) => sum + file.size, 0),
		perFileBytes: files.map(() => 0),
	};
}

/** Percentage (0..100) of sent/total; 0 for an empty total, clamped against overshoot. */
export function percentOf(sent: number, total: number): number {
	if (total <= 0) {
		return 0;
	}
	return Math.min(100, (sent / total) * 100);
}

const SPEED: Intl.NumberFormatOptions = {
	style: "unit", unit: "megabyte-per-second", unitDisplay: "short",
	minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false,
};

const ETA_SECONDS: Intl.NumberFormatOptions = {style: "unit", unit: "second", unitDisplay: "narrow", maximumFractionDigits: 0, useGrouping: false};
const WHOLE: Intl.NumberFormatOptions = {maximumFractionDigits: 0, useGrouping: false};
const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Design `speedStr`: decimal megabytes per second with one locale-aware decimal digit. */
export function formatSpeed(bytesPerSecond: number, locale: Locale): string {
	return numberFormat(locale, SPEED).format(bytesPerSecond / 1_000_000);
}

/**
 * Time left, in the largest unit that reads naturally (kitecorp/transferify-ui#2888; it counted only in
 * minutes, so a slow 500 MB upload read "4800m left"): whole seconds up to a minute, "12 min" under an
 * hour, "1 h 20 min" under a day (no "0 min"), and whole days from a day, which the catalog words
 * (`days`) so each language has its own plural. "h" and "min" are the same symbols in every language
 * here; the numbers are the locale's.
 */
export function formatEta(seconds: number, locale: Locale, days: (count: number) => string): string {
	if (seconds <= MINUTE) {
		return numberFormat(locale, ETA_SECONDS).format(Math.max(0, seconds));
	}
	const whole = numberFormat(locale, WHOLE);
	if (seconds >= DAY) {
		return days(Math.max(1, Math.round(seconds / DAY)));
	}
	const minutes = Math.round(seconds / MINUTE);
	if (minutes < 60) {
		return `${whole.format(minutes)} min`;
	}
	const hours = Math.floor(minutes / 60), rest = minutes % 60;
	if (hours >= 24) {
		return days(1);
	}
	return rest === 0 ? `${whole.format(hours)} h` : `${whole.format(hours)} h ${whole.format(rest)} min`;
}

/** The catalog's words the time-left line is built from; the copy never lives here. */
export interface EtaWords {
	/** "{eta} left", in the language's own order. */
	timeLeft: (eta: string) => string;
	/** A whole number of days, with its plural. */
	days: (count: number) => string;
}

/**
 * How coarsely the upload announces itself: every {@link ANNOUNCEMENT_PERCENT_STEP} of overall
 * progress, and never below the first step.
 *
 * The card's polite live region cannot simply carry the visible readout — that text is rewritten
 * every frame while it travels, and a live region announcing continuously tells a screen-reader
 * user nothing at all. This is the cadence that reaches THEM, deliberately much coarser than the
 * one that reaches the eye.
 */
export const ANNOUNCEMENT_PERCENT_STEP = 10;

/** The step a percentage belongs to; 0 means "not far enough in to be worth saying". */
export function announcementStep(percent: number): number {
	const step = Math.floor(percent / ANNOUNCEMENT_PERCENT_STEP) * ANNOUNCEMENT_PERCENT_STEP;
	return step < ANNOUNCEMENT_PERCENT_STEP ? 0 : step;
}

/**
 * The whole rate readout as one line, shared by the visible gauge caption and its catalog-composed
 * `aria-valuetext`. By default that line is just the time left ("8m left"); the speed prefix
 * ("11.6 MB/s · ") is an internal debug readout since ui#1840, so `showSpeed` comes from
 * {@link core/debugFlags.uploadSpeedDebugEnabled} rather than ever being true for a user.
 *
 * @param words the catalog's own "{eta} left" wrapper and day plural — the copy never lives here
 */
export function formatRate(
	bytesPerSecond: number,
	etaSeconds: number,
	locale: Locale,
	words: EtaWords,
	showSpeed: boolean,
): string {
	const left = words.timeLeft(formatEta(etaSeconds, locale, words.days));
	return showSpeed ? `${formatSpeed(bytesPerSecond, locale)} · ${left}` : left;
}
